/**
 * API Service for Web
 * Handles direct communication with the API server
 * Adapted from mobile app patterns for browser environment
 */

import { ShootingSession } from '../types';
import { API_CONFIG, getApiUrl } from '../config/api';
import { authService } from './authService';

class ApiService {
  private get sessionsUrl(): string {
    return `${getApiUrl('sync')}`;
  }

  /**
   * Fetch with timeout wrapper (matching mobile app pattern)
   */
  private async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include', // Important: include cookies for Azure Easy Auth
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      throw error;
    }
  }

  async getUserId(): Promise<string> {
    const user = authService.getUser();
    if (!user || !user.id) {
      throw new Error('User not authenticated');
    }
    return user.id;
  }

  /**
   * Get all sessions from the server
   */
  async getAllSessions(): Promise<ShootingSession[]> {
    try {
      const userId = await this.getUserId();
      const url = `${this.sessionsUrl}/${userId}`;

      // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
      const response = await authService.makeAuthenticatedRequest(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        // If unauthorized, user needs to re-authenticate
        if (response.status === 401) {
          await authService.signOut();
          throw new Error('Session expired. Please sign in again.');
        }
        
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      const sessions: ShootingSession[] = data.sessions || [];
      return sessions
        .filter(s => !s.isDeleted)
        .sort((a, b) => b.date - a.date);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a single session by ID
   */
  async getSession(id: string): Promise<ShootingSession | null> {
    try {
      const sessions = await this.getAllSessions();
      return sessions.find(s => s.id === id) || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Save a new session to the server
   */
  async saveSession(session: Omit<ShootingSession, 'id'>): Promise<string> {
    try {
      const userId = await this.getUserId();
      const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const sessionWithId: ShootingSession = {
        ...session,
        id,
        userId,
        lastModified: Date.now(),
        syncVersion: 1,
        deviceId: 'web-browser',
      };

      // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
      const response = await authService.makeAuthenticatedRequest(this.sessionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          deviceId: 'web-browser',
          sessions: [sessionWithId],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save session: ${response.status}`);
      }

      return id;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing session on the server
   */
  async updateSession(id: string, updates: Partial<ShootingSession>): Promise<void> {
    try {
      const userId = await this.getUserId();
      const sessions = await this.getAllSessions();
      const session = sessions.find(s => s.id === id);

      if (!session) {
        throw new Error(`Session ${id} not found`);
      }

      // Merge updates with existing session
      const updatedSession: ShootingSession = {
        ...session,
        ...updates,
        userId,
        lastModified: Date.now(),
        syncVersion: (session.syncVersion || 1) + 1,
        deviceId: 'web-browser',
      };

      // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
      const response = await authService.makeAuthenticatedRequest(this.sessionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          deviceId: 'web-browser',
          sessions: [updatedSession],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update session: ${response.status}`);
      }
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a session from the server (soft delete)
   */
  async deleteSession(id: string): Promise<void> {
    try {
      const userId = await this.getUserId();
      
      // Fetch all sessions including deleted ones to find the target session
      const url = `${this.sessionsUrl}/${userId}`;
      // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
      const response = await authService.makeAuthenticatedRequest(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch session: ${response.status}`);
      }

      const data = await response.json();
      const sessions: ShootingSession[] = data.sessions || [];
      const session = sessions.find(s => s.id === id);

      if (session) {
        // Mark as deleted (soft delete)
        session.isDeleted = true;
        session.lastModified = Date.now();
        session.syncVersion = (session.syncVersion || 1) + 1;

        // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
        const deleteResponse = await authService.makeAuthenticatedRequest(this.sessionsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            deviceId: 'web-browser',
            sessions: [session],
          }),
        });

        if (!deleteResponse.ok) {
          const errorText = await deleteResponse.text();
          throw new Error(`Failed to delete session: ${deleteResponse.status}`);
        }
      }
    } catch (error: any) {
      throw error;
    }
  }
}

export const apiService = new ApiService();
